/**
 * Animal Alphabet (early literacy) — scripted play-simulation beats (EN/ES).
 * WonderGuidance models the 4 skills + sustainability; user does child then caregiver.
 */

import type { Language } from '@/lib/voiceManager';
import { getPlayTogetherPostSimHandoff } from './playTogetherMicroOnboarding';

type L = Language;

/** Momento 0 — Frame */
export function getLiteracyBeat0Frame(lang: L): string {
  return lang === 'en'
    ? [
        'Quick **Animal ABCs** round: you’ll be the **child** first, then the **caregiver**.',
        '',
        '**Name an animal** from the alphabet (for example: alligator, bear, cat). Type or use voice.',
      ].join('\n')
    : [
        'Ronda breve de **Animal ABCs**: **primero** el **niño/a**, **después** el **cuidador/a**.',
        '',
        '**Di o escribe un animal** del abecedario (por ejemplo: caimán, oso, gato). Puedes escribir o usar la voz.',
      ].join('\n');
}

/** First letter for prompts (simple: first character of first word) */
export function firstLetterHint(animal: string): string {
  const w = animal.trim();
  if (!w) return '—';
  return w[0]!.toUpperCase();
}

/**
 * Momento 1 — Demonstration lines + parenthetical skill notes (per product spec).
 * Tone is in the lines themselves; relate stays tied to **${animal}**, letter, and Animal ABCs—no generic wind/trees.
 */
export function getLiteracyBeat1Skills(lang: L, animalRaw: string): string {
  const animal = animalRaw.trim() || (lang === 'en' ? 'your animal' : 'tu animal');
  const letter = firstLetterHint(animalRaw);
  return lang === 'en'
    ? [
        'As the caregiver using the **four skills** and **sustainability learning**, I would say:',
        '',
        `You chose the animal **${animal}** (Describe skill: cheer that you are wondering out loud)`,
        '',
        `**${animal}**—and the **${letter}** sound (Reflect skill: mirror the child’s play energy)`,
        '',
        `I love that **${animal}** is your selection! (Praise skill: highlight what the child did in a positive way)`,
        '',
        `I wonder where **${animal}** lives—maybe in a forest or by water? We can care for real animals by learning about their homes. (Relate skill: add a connecting thought about nature or the world)`,
        '',
        'Which line felt strongest—or the **whole demonstration** together? (Tap a button or type.)',
      ].join('\n')
    : [
        'Como cuidador/a usando las **cuatro habilidades** y el **aprendizaje en sostenibilidad**, diría:',
        '',
        `Elegiste el animal **${animal}** (habilidad Describir: celebrar que lo dijiste en voz alta)`,
        '',
        `**${animal}**—y el sonido de la **${letter}** (habilidad Reflejar: espejar la energía de juego del niño/a)`,
        '',
        `¡Me encanta que **${animal}** sea tu elección! (habilidad Elogiar: destacar en positivo lo que hizo el niño/a)`,
        '',
        `Me pregunto dónde vive **${animal}**—¿tal vez en un bosque o cerca del agua? Podemos cuidar animales reales aprendiendo sobre sus hogares. (habilidad Relacionar: añade un pensamiento que conecta con la naturaleza o el mundo)`,
        '',
        '¿Qué frase te resonó más, o la **demostración entera**? (Toca un botón o escribe.)',
      ].join('\n');
}

/** Labels for skill-choice chips */
export const literacySkillChoiceLabels = [
  { id: 'describe' as const, en: 'Describe', es: 'Describir' },
  { id: 'reflect' as const, en: 'Reflect', es: 'Reflejar' },
  { id: 'praise' as const, en: 'Praise', es: 'Elogiar' },
  { id: 'relate' as const, en: 'Relate', es: 'Relacionar' },
  { id: 'all_four' as const, en: 'All 4 skills', es: 'Las 4 habilidades' },
];

/** Observe/Describe, Reflect, Praise, Relate + nature — shared by assessment and coach copy. */
export function getLiteracyCaregiverSignals(text: string, lang: L) {
  const raw = text.trim();
  const t = raw.toLowerCase();
  const isEn = lang === 'en';

  const describe = isEn
    ? /\b(i see|you('re|r)|your|notice|noticed|hear|heard|sound|sounds|saying|look|looking|big|loud|soft|grin|face|eyes|voice|grrr)\b/i.test(t) ||
      /\bhow\s+(loud|soft|fast|big)\b/i.test(t)
    : /\b(veo|noto|notas|escucho|escuchas|dices|dijo|miras|mira|suena|grande|cara|ojos|voz)\b/i.test(t);

  const reflect = isEn
    ? /\b(like|same|together|me too|tap|echo|with you|rawr|too)\b/i.test(t) ||
      /\bbears?\b|bear+/i.test(raw) ||
      /\bi love\b/i.test(raw)
    : /\b(como|igual|juntos|contigo|también|rawr)\b/i.test(t) ||
      /\bosos?\b|oso+/i.test(raw) ||
      /\b(me encanta|amo)\b/i.test(t);

  const praise = isEn
    ? /\b(love|great|good|nice|brave|clever|proud|happy|wow|yes|fun|try)\b/i.test(t)
    : /\b(me encanta|genial|bien|buen|valiente|alegro|feliz|sí|guay|intenta)\b/i.test(t);

  const relate = isEn
    ? /\b(remind|remember|add|also|maybe|story|home|feel|think|wonder)\b/i.test(t) ||
      /you know what/i.test(raw)
    : /\b(recuerda|además|también|cuento|casa|siento|pienso|imagina)\b/i.test(t) ||
      /sabes qué/i.test(raw);

  const nature = isEn
    ? /\b(nature|tree|trees|forest|earth|plant|water|animal|animals|sky|leaf|leaves|bee|bees|birds|flower|grow|green|outside|planet|care|place|wood|river|garden|honey)\b/i.test(t) ||
      /\bbears?\b|bear+/i.test(raw)
    : /\b(naturaleza|árbol|árboles|bosque|tierra|planta|agua|animal|animales|cielo|hoja|flor|verde|afuera|planeta|cuidar|lugar|río|jardín|miel)\b/i.test(t) ||
      /\bosos?\b|oso+/i.test(raw);

  return { describe, reflect, praise, relate, nature };
}

/**
 * Lightweight check: did the caregiver’s reply likely use Observe/Describe, Reflect, Praise, Relate + nature?
 * Not perfect—enough to trigger supportive coaching when the reply is very thin.
 */
export function assessLiteracyCaregiverResponse(text: string, lang: L): { level: 'weak' | 'ok' | 'strong' } {
  const raw = text.trim();
  if (raw.length < 22) return { level: 'weak' };

  const { describe, reflect, praise, relate, nature } = getLiteracyCaregiverSignals(text, lang);

  let score = 0;
  if (describe) score += 1;
  if (reflect) score += 1;
  if (praise) score += 1;
  if (relate) score += 1;
  if (nature) score += 1;

  if (score >= 4 && raw.length >= 35) return { level: 'strong' };
  if (score >= 3 && raw.length >= 30) return { level: 'ok' };
  if (score <= 2 || raw.length < 28) return { level: 'weak' };
  if (!nature && score <= 3 && raw.length < 55) return { level: 'weak' };
  return { level: 'ok' };
}

/** Full wrap-up coach message (no quoted user text). Strong = celebratory 4-skills summary; else one focused tip. */
function buildLiteracyCoachingFeedback(lang: L, caregiverPracticeRaw: string): string {
  const raw = caregiverPracticeRaw.replace(/\s+/g, ' ').trim();
  const { describe, reflect, praise, relate, nature } = getLiteracyCaregiverSignals(raw, lang);

  const gapsEn: string[] = [];
  if (!describe)
    gapsEn.push(
      '**Describe:** On your next try, add **one** concrete detail right after **“Bear! Rawr!”**—what you *see or hear*—**before** the next idea.'
    );
  if (!reflect && !praise)
    gapsEn.push(
      '**Reflect & praise:** Add a short mirror of their energy, then one specific **praise**—helps the child feel met, not managed.'
    );
  else if (!reflect)
    gapsEn.push('**Reflect:** One short echo of their energy (e.g. “I hear that bear with you!”) tightens the pair.');
  else if (!praise)
    gapsEn.push('**Praise:** Name one thing you value in what they did—specific beats generic.');
  if (!nature)
    gapsEn.push(
      '**Relate (caring for life):** Add one thought about the animal in the real world—where it lives, what it needs, or how we help care for it. Example: "Bears live in forests that we can help protect."'
    );
  if (!relate && nature && praise)
    gapsEn.push(
      '**Relate:** A short bridge to the next idea (e.g. bees and flowers) keeps the thread going without a long aside.'
    );

  if (lang === 'en') {
    if (gapsEn.length === 0) {
      return [
        '**Coaching feedback:** Your reply fits this practice: after **“Bear! Rawr!”** you used **describe**, **reflect**, **praise**, and **relate** together—concrete detail, matching energy, appreciation, and a tie toward animals, plants, or place, with room for what comes next.',
        '',
        'That habit maps cleanly to the games below; letters and animals there work best as **connection**, not drills.',
      ].join('\n');
    }
    const strengthBits: string[] = [];
    if (describe) strengthBits.push('concrete detail');
    if (reflect) strengthBits.push('matched the play energy');
    if (praise) strengthBits.push('specific appreciation');
    if (nature) strengthBits.push('nature or home');
    if (relate) strengthBits.push('a connecting idea');
    const strengthLine =
      strengthBits.length > 0
        ? `**Summary:** This line includes: ${strengthBits.join('; ')}.`
        : '**Summary:** You completed the caregiver round.';

    return ['**Coaching feedback:**', '', strengthLine, '', gapsEn[0]].join('\n');
  }

  const gapsEs: string[] = [];
  if (!describe)
    gapsEs.push(
      '**Describir:** En el próximo intento, añade **un** detalle concreto justo después de **“¡Oso! ¡Rawr!”**—lo que *ves u oyes*—**antes** de la siguiente idea.'
    );
  if (!reflect && !praise)
    gapsEs.push(
      '**Reflejar y elogiar:** Un eco breve de su energía y un **elogio** concreto ayudan a que se sienta acompañado/a.'
    );
  else if (!reflect)
    gapsEs.push('**Reflejar:** Una frase corta que devuelva su energía (p. ej. “¡Escucho ese oso contigo!”) cierra el par.');
  else if (!praise)
    gapsEs.push('**Elogiar:** Nombra algo que **valores** de lo que hizo—concreto, no genérico.');
  if (!nature)
    gapsEs.push(
      '**Relacionar (cuidar la vida):** Añade un pensamiento sobre el animal en el mundo real—dónde vive, qué necesita o cómo lo cuidamos. Ejemplo: "Los osos viven en bosques que podemos ayudar a proteger."'
    );
  if (!relate && nature && praise)
    gapsEs.push(
      '**Relacionar:** Un puente corto a la siguiente idea (p. ej. abejas y flores) mantiene el hilo sin un aparte largo.'
    );

  if (gapsEs.length === 0) {
    return [
      '**Retroalimentación:** Tu respuesta encaja en esta práctica: tras **“¡Oso! ¡Rawr!”** usaste **describir**, **reflejar**, **elogiar** y **relacionar** juntas—detalle concreto, energía alineada, reconocimiento y lazo hacia animales, plantas o lugar, con espacio para lo que sigue.',
      '',
      'Ese hábito se traduce bien a los juegos de abajo; letras y animales funcionan mejor como **conexión**, no como examen.',
    ].join('\n');
  }
  const strengthBitsEs: string[] = [];
  if (describe) strengthBitsEs.push('detalle concreto');
  if (reflect) strengthBitsEs.push('energía alineada al juego');
  if (praise) strengthBitsEs.push('reconocimiento específico');
  if (nature) strengthBitsEs.push('naturaleza u hogar');
  if (relate) strengthBitsEs.push('idea que conecta');
  const strengthLineEs =
    strengthBitsEs.length > 0
      ? `**Resumen:** Esta línea incluye: ${strengthBitsEs.join('; ')}.`
      : '**Resumen:** Completaste la ronda de cuidador/a.';

  return ['**Retroalimentación:**', '', strengthLineEs, '', gapsEs[0]].join('\n');
}

/** Second coaching round — tighter checklist before we move on */
export function getLiteracyConstructiveCaregiverFeedbackRound2(lang: L): string {
  return lang === 'en'
    ? [
        '**Coaching feedback:** One more pass as the caregiver. Let\'s build it step by step:',
        '',
        '**Start with:** "I hear..." or "You said..." (what you notice)',
        '**Then add:** "Bear! Rawr!" (mirror the energy)',
        '**Then praise:** "I love how..." (something specific)',
        '**End with:** "Bears..." (where they live, how we care)',
        '',
        '**Example:** "I hear that powerful bear roar! Bear—rawr! I love how you\'re using your whole voice for the B sound. Bears need forests to live in, and learning about them helps us care for their homes."',
        '',
        '**Your turn—send 2–3 sentences when ready.**',
      ].join('\n')
    : [
        '**Retroalimentación:** Un intento más como cuidador/a. Construyámoslo paso a paso:',
        '',
        '**Empieza con:** "Escucho..." o "Dijiste..." (lo que notas)',
        '**Luego añade:** "¡Oso! ¡Rawr!" (refleja la energía)',
        '**Luego elogia:** "Me encanta cómo..." (algo específico)',
        '**Termina con:** "Los osos..." (dónde viven, cómo cuidamos)',
        '',
        '**Ejemplo:** "¡Escucho ese rugido poderoso de oso! Oso—¡rawr! Me encanta cómo usas toda tu voz para el sonido de la O. Los osos necesitan bosques para vivir, y aprender de ellos nos ayuda a cuidar sus hogares."',
        '',
        '**Tu turno—envía 2–3 frases cuando quieras.**',
      ].join('\n');
}

/** When we advance after max coaching but the reply was still thin */
export function getLiteracyCaregiverSoftLanding(lang: L): string {
  return lang === 'en'
    ? [
        '**Coaching feedback:** We’ll move on from here—real play stays messy, and that’s normal.',
        '',
        'The games below are the next step when you’re ready.',
      ].join('\n')
    : [
        '**Retroalimentación:** Seguimos desde aquí—el juego real sigue siendo imperfecto, y es normal.',
        '',
        'Los juegos de abajo son el siguiente paso cuando quieras.',
      ].join('\n');
}

/** When the caregiver reply looks thin: why the skills matter together + one more try */
export function getLiteracyConstructiveCaregiverFeedback(lang: L): string {
  return lang === 'en'
    ? [
        '**Coaching feedback:** These four moves stack: **describe** (something concrete you notice), **reflect** (match their energy), **praise** (something specific), **relate** (connect to the real world—where the animal lives, how we care for it). Together they keep literacy playful, not test-like.',
        '',
        '**Here\'s an example** for "Bear! Rawr!":',
        '"I hear that big bear sound! Bear—rawr! I love how you\'re making the B sound loud and strong. Bears live in forests, and when we learn about them, we\'re caring for real animals."',
        '',
        '**Try again** in **2–3 short sentences** after my bear line. Include:',
        '1️⃣ **What you notice** (the sound, the energy)',
        '2️⃣ **Mirror it back** ("Bear! Rawr!")',
        '3️⃣ **Praise something specific** (the volume, the B sound)',
        '4️⃣ **Connect to real bears** (where they live, how we care)',
        '',
        '**Send** when ready.',
      ].join('\n')
    : [
        '**Retroalimentación:** Las cuatro se apoyan: **describir** (algo concreto que notas), **reflejar** (energía alineada), **elogiar** (algo específico), **relacionar** (conectar con el mundo real—dónde vive el animal, cómo lo cuidamos). Juntas mantienen la lectura jugada, no tipo examen.',
        '',
        '**Aquí un ejemplo** para "¡Oso! ¡Rawr!":',
        '"¡Escucho ese gran sonido de oso! Oso—¡rawr! Me encanta cómo haces el sonido de la O fuerte y claro. Los osos viven en bosques, y cuando aprendemos de ellos, estamos cuidando animales reales."',
        '',
        '**Otro intento** en **2–3 frases** tras mi oso. Incluye:',
        '1️⃣ **Lo que notas** (el sonido, la energía)',
        '2️⃣ **Devuélvelo** ("¡Oso! ¡Rawr!")',
        '3️⃣ **Elogia algo específico** (el volumen, el sonido)',
        '4️⃣ **Conecta con osos reales** (dónde viven, cómo cuidamos)',
        '',
        '**Envía** cuando quieras.',
      ].join('\n');
}

/** Momento 2 — Caregiver role */
export function getLiteracyBeat2Caregiver(lang: L): string {
  return lang === 'en'
    ? [
        "**Switch:** I'm the **child** now.",
        '',
        'I say: **Bear! Rawr!**',
        '',
        'Your turn: in **2–3 short sentences**, **name** what you notice about my line, **match** my play-energy, **say something you genuinely appreciate**, and **add one thought** about where bears live or how we care for them (connecting to the real world).',
        '',
        '**Write or say** your response.',
      ].join('\n')
    : [
        '**Cambio:** ahora yo soy el **niño/a**.',
        '',
        'Digo: **¡Oso! ¡Rawr!**',
        '',
        'Tu parte: en **2–3 frases cortas**, **di** qué notas en mi línea, **acompaña** mi energía de juego, **elogia** algo concreto de verdad y **añade un pensamiento** sobre dónde viven los osos o cómo los cuidamos (conectando con el mundo real).',
        '',
        '**Escribe o di** tu respuesta.',
      ].join('\n');
}

/**
 * After caregiver practice: coaching feedback only (no quoted user text; CTAs live in the games step UI).
 */
export function getLiteracyAfterCaregiverWrapUp(lang: L, caregiverPracticeRaw: string): string {
  const normalized = caregiverPracticeRaw.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return lang === 'en'
      ? [
          '**Coaching feedback:** No text came through—try **2–3 short sentences** next round after the child line so we can check the four skills against your reply.',
        ].join('\n')
      : [
          '**Retroalimentación:** No llegó texto—prueba **2–3 frases** la próxima vez tras la línea del niño/a para revisar las cuatro habilidades.',
        ].join('\n');
  }
  return buildLiteracyCoachingFeedback(lang, caregiverPracticeRaw);
}

/** Momento 3 — Feedback + app tie + numbers (after caregiver practice) */
export function getLiteracyBeat3Feedback(lang: L): string {
  return lang === 'en'
    ? [
        'Here’s how that rhythm can land: **notice** what stands out; **stay with** my sound or movement; **appreciate** something real; **connect** to nature or home.',
        '',
        '**Animal Alphabet:** In the app, the loop is **tap the letter—hear the animal—stay playful**—same idea.',
        '',
        '**Numbers:** Same rhythm—**notice—mirror—praise—connect** (e.g. counting flowers, steps, birds). In **Counting Nature**, **1–20** pairs with nature images—still **child-led**.',
        '',
        'When you open **Animal ABCs, 123s**, start with **Animal Alphabet** or **Count with Nature**—whichever fits your child today.',
      ].join('\n')
    : [
        'Así puede sonar el ritmo: **notar** lo que destaca; **quedarte** con mi sonido o movimiento; **valorar** algo real; **conectar** con la naturaleza o el hogar.',
        '',
        '**Animal Alphabet:** En la app, el bucle es **tocar la letra—escuchar el animal—seguir jugando**—la misma idea.',
        '',
        '**Números:** El mismo ritmo—**notar—reflejar—elogiar—conectar** (p. ej., contar flores, pasos, pájaros). En **Contar con naturaleza**, **1–20** va con imágenes de la naturaleza—sigue **liderado por el niño/a**.',
        '',
        'Al abrir **Animal ABCs, 123**, empieza por **Animal Alphabet** o **Contar con naturaleza**—lo que hoy encaje con tu niño/a.',
      ].join('\n');
}

/** After caregiver practice: explicit open-game cue + handoff to the cards. */
export function getLiteracyBeat4Readiness(lang: L): string {
  return lang === 'en'
    ? [
        '**Open the game:** Tap the highlighted **Animal ABCs, 123s** card—or **Open Animal ABCs** if you see it—for a **2–3 minute** round and keep the same rhythm.',
        '',
        getPlayTogetherPostSimHandoff(lang),
      ].join('\n')
    : [
        '**Abre el juego:** Toca la tarjeta **ABC de Animales, 123** resaltada—o **Abrir ABCs de animales** si aparece—para una **ronda de 2–3 minutos** y seguir el mismo ritmo.',
        '',
        getPlayTogetherPostSimHandoff(lang),
      ].join('\n');
}
